import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { config } from '@/utils/config'
import { setFormatterLocale } from '@/utils/formatters'

export interface Language {
  code: string
  name: string
  nativeName: string
  flag: string
  rtl: boolean
}

interface TranslationObject {
  [key: string]: string | TranslationObject
}

/**
 * Başlangıç dilini belirle (sadece localStorage'dan)
 * API'den dil listesi gelmeden önce kullanılır
 */
const getInitialLanguage = (): string => {
  return localStorage.getItem('app_language') || 'tr'
}

export const useLanguageStore = defineStore('language', () => {
  // State
  const languages = ref<Language[]>([])
  const currentLanguage = ref<string>(getInitialLanguage())
  const translations = ref<TranslationObject>({})
  const loading = ref(false)
  const translationsLoading = ref(false)
  const isChangingLanguage = ref(false)

  // Watch language changes and persist to localStorage
  watch(currentLanguage, (newLang) => {
    localStorage.setItem('app_language', newLang)
    // Update formatter locale
    setFormatterLocale(newLang)
    // Update document direction for RTL languages
    const lang = languages.value.find(l => l.code === newLang)
    if (lang) {
      document.documentElement.dir = lang.rtl ? 'rtl' : 'ltr'
      document.documentElement.lang = newLang
    }
    // Note: fetchTranslations is called in setLanguage, not here to avoid race conditions
  })
  
  // Initialize formatter locale with current language
  setFormatterLocale(currentLanguage.value)

  /**
   * Tarayıcı dilini algıla (desteklenen diller listesiyle karşılaştır)
   */
  const detectBrowserLanguage = (supportedCodes: string[]): string | null => {
    const browserLangs = [navigator.language, ...(navigator.languages || [])]
    
    for (const lang of browserLangs) {
      const langCode = lang.split('-')[0].toLowerCase()
      if (supportedCodes.includes(langCode)) {
        return langCode
      }
    }
    return null
  }

  /**
   * Desteklenen dil kodlarını al
   */
  const getSupportedLanguageCodes = (): string[] => {
    return languages.value.map(l => l.code)
  }

  // Actions
  const fetchLanguages = async () => {
    if (languages.value.length > 0) return languages.value
    
    loading.value = true
    try {
      const response = await fetch(`${config.translateApiUrl}/languages`)
      const data = await response.json()
      
      if (data.languages && Array.isArray(data.languages)) {
        languages.value = data.languages
      }
    } catch (error) {
      console.error('Diller yüklenirken hata:', error)
      // Fallback languages
      languages.value = [
        { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷', rtl: false },
        { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', rtl: false },
        { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', rtl: false },
        { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', rtl: false },
        { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', rtl: false },
        { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', rtl: false },
        { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱', rtl: false },
        { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', rtl: false },
        { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', rtl: false },
        { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', rtl: true },
      ]
    } finally {
      loading.value = false
    }
    
    // Dil listesi yüklendikten sonra, daha önce dil seçilmemişse tarayıcı dilini otomatik algıla
    const savedLang = localStorage.getItem('app_language')
    const supportedCodes = getSupportedLanguageCodes()
    
    if (!savedLang || !supportedCodes.includes(savedLang)) {
      // Daha önce dil seçilmemiş veya geçersiz - tarayıcı dilini algıla
      const detectedLang = detectBrowserLanguage(supportedCodes)
      if (detectedLang && detectedLang !== currentLanguage.value) {
        console.log(`🌍 Tarayıcı dili algılandı: ${detectedLang}`)
        currentLanguage.value = detectedLang
        localStorage.setItem('app_language', detectedLang)
      } else if (!detectedLang && currentLanguage.value !== 'tr') {
        // Tarayıcı dili desteklenmiyorsa varsayılan 'tr' kullan
        currentLanguage.value = 'tr'
        localStorage.setItem('app_language', 'tr')
      }
    }
    
    return languages.value
  }

  // Fetch translations for a specific language
  const fetchTranslations = async (lang?: string) => {
    const targetLang = lang || currentLanguage.value
    translationsLoading.value = true
    
    try {
      const response = await fetch(`${config.translateApiUrl}/company-panel/${targetLang}`)
      const data = await response.json()
      
      if (data.translations) {
        translations.value = data.translations
        // İlk yüklemeden sonra eksik anahtar loglaması aktif olsun
        translationsLoaded.value = true
        // Önceki eksik anahtarları temizle (artık gerçekten eksik olanları görmek için)
        missingKeys.clear()
      }
    } catch (error) {
      console.error('Çeviriler yüklenirken hata:', error)
      // Fallback to Turkish translations
      translations.value = getFallbackTranslations()
      translationsLoaded.value = true
    } finally {
      translationsLoading.value = false
    }
  }

  // Get fallback Turkish translations
  const getFallbackTranslations = (): TranslationObject => ({
    login: {
      title: 'Giriş Yap',
      subtitle: 'Personel Takip Sistemi',
      welcome: 'Lütfen bilgilerinizle giriş yapın',
      email: 'E-posta adresiniz',
      password: 'Şifreniz',
      rememberMe: 'Beni Hatırla',
      forgotPassword: 'Şifremi Unuttum',
      submit: 'Giriş Yap',
      noAccount: 'Hesabınız yok mu?',
      signUp: 'Yeni Üyelik Oluştur',
      footer: 'Tüm hakları saklıdır.',
      forgotPasswordTitle: 'Şifremi Unuttum',
      forgotPasswordMessage: 'Şifre sıfırlama bağlantısı gönderilecek e-posta adresinizi giriniz',
      send: 'Gönder',
      cancel: 'İptal',
      invalidEmail: 'Geçerli bir e-posta adresi giriniz',
      success: 'Başarılı',
      resetLinkSent: 'adresine şifre sıfırlama bağlantısı gönderildi.'
    },
    validation: {
      emailRequired: 'E-posta adresi gereklidir',
      emailInvalid: 'Geçerli bir e-posta adresi giriniz',
      passwordRequired: 'Şifre gereklidir',
      passwordMinLength: 'Şifre en az 6 karakter olmalıdır'
    },
    auth: {
      loginFailed: 'Giriş başarısız!'
    },
    errors: {
      loginFailed: 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.',
      generalError: 'Bir hata oluştu. Lütfen daha sonra tekrar deneyin.'
    },
    error_codes: {
      '10013': 'Sunucu hatası, lütfen daha sonra tekrar deneyin'
    },
    common: {
      loading: 'Yükleniyor...',
      save: 'Kaydet',
      cancel: 'İptal',
      delete: 'Sil',
      edit: 'Düzenle',
      add: 'Ekle',
      search: 'Ara',
      yes: 'Evet',
      no: 'Hayır',
      confirm: 'Onayla',
      close: 'Kapat',
      refresh: 'Yenile',
      all: 'Tümü',
      date: 'Tarih',
      actions: 'İşlemler'
    },
    sidebar: {
      suggestions: 'Fikir ve Öneriler'
    },
    suggestions: {
      title: 'Fikir ve Öneriler',
      detail: 'Öneri Detayı',
      filter: {
        readStatus: 'Okunma Durumu',
        employee: 'Personel'
      },
      read: 'Okundu',
      unread: 'Okunmadı',
      stats: {
        total: 'Toplam',
        unread: 'Okunmamış',
        read: 'Okunmuş'
      },
      employee: 'Personel',
      text: 'Öneri/Fikir',
      readStatus: 'Durum',
      markAsRead: 'Okundu İşaretle',
      markAsUnread: 'Okunmadı İşaretle',
      markedAsRead: 'Okundu olarak işaretlendi',
      markedAsUnread: 'Okunmadı olarak işaretlendi',
      markError: 'İşaretleme hatası',
      loadError: 'Öneriler yüklenemedi',
      edit: 'Öneri Düzenle',
      createTitle: 'Yeni Fikir/Öneri Ekle',
      textNotEditable: 'Öneri metni düzenlenemez. Sadece okunma durumu değiştirilebilir.',
      selectEmployee: 'Personel seçin',
      updateSuccess: 'Öneri güncellendi',
      updateError: 'Öneri güncellenirken bir hata oluştu',
      createSuccess: 'Öneri başarıyla gönderildi',
      createError: 'Öneri gönderilirken bir hata oluştu',
      textRequired: 'Öneri metni gereklidir',
      textMinLength: 'Öneri metni en az 5 karakter olmalıdır',
      submit: 'Gönder',
      filterHint: 'Öneri metni, personel adı ara...',
      noSuggestions: 'Henüz öneri bulunmuyor',
      textHint: 'Fikir veya önerinizi detaylı bir şekilde yazın...',
      noResults: 'Filtre kriterlerine uygun öneri bulunamadı',
      clearFilters: 'Filtreleri Temizle'
    },
    days: {
      sun: 'Paz',
      mon: 'Pzt',
      tue: 'Sal',
      wed: 'Çar',
      thu: 'Per',
      fri: 'Cum',
      sat: 'Cmt',
      sunday: 'Pazar',
      monday: 'Pazartesi',
      tuesday: 'Salı',
      wednesday: 'Çarşamba',
      thursday: 'Perşembe',
      friday: 'Cuma',
      saturday: 'Cumartesi'
    }
  })

  const setLanguage = async (langCode: string) => {
    if (langCode === currentLanguage.value) return
    
    // Animasyon başlat
    isChangingLanguage.value = true
    
    // Overlay göründükten sonra çeviriyi yükle
    await new Promise(resolve => setTimeout(resolve, 200))
    
    currentLanguage.value = langCode
    await fetchTranslations(langCode)
    
    // Çeviriler yüklendikten sonra biraz bekle (animasyon görünsün)
    await new Promise(resolve => setTimeout(resolve, 300))
    isChangingLanguage.value = false
  }

  const getCurrentLanguageData = () => {
    return languages.value.find(l => l.code === currentLanguage.value) || null
  }

  // Eksik çevirileri takip etmek için Set
  const missingKeys = new Set<string>()
  
  // Çeviriler en az bir kez yüklendi mi?
  const translationsLoaded = ref(false)

  // Translation helper - get nested translation by key path (e.g., "login.title")
  const t = (key: string, fallback?: string): string => {
    // error_codes.xxx formatı için özel kontrol
    if (key.startsWith('error_codes.')) {
      const errorCode = key.replace('error_codes.', '')
      const errorCodes = translations.value.error_codes as Record<string, string> | undefined
      if (errorCodes && errorCodes[errorCode]) {
        return errorCodes[errorCode]
      }
      // Eksik error code çevirisi için logla
      if (translationsLoaded.value && !missingKeys.has(key)) {
        missingKeys.add(key)
        console.warn(`🔑 Eksik çeviri: "${key}" → "${fallback || key}"`)
      }
      return fallback || key
    }
    
    const keys = key.split('.')
    let result: any = translations.value
    
    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k]
      } else {
        // Eksik anahtarı logla (sadece çeviriler yüklendikten sonra ve bir kez)
        if (translationsLoaded.value && !missingKeys.has(key)) {
          missingKeys.add(key)
          console.warn(`🔑 Eksik çeviri: "${key}" → "${fallback || key}"`)
        }
        return fallback || key
      }
    }
    
    if (typeof result !== 'string') {
      if (translationsLoaded.value && !missingKeys.has(key)) {
        missingKeys.add(key)
        console.warn(`🔑 Çeviri string değil: "${key}" → "${fallback || key}"`)
      }
      return fallback || key
    }
    
    return result
  }

  // Eksik anahtarları al (debug için)
  const getMissingKeys = () => Array.from(missingKeys)
  
  // Eksik anahtarları temizle
  const clearMissingKeys = () => missingKeys.clear()

  return {
    languages,
    currentLanguage,
    translations,
    loading,
    translationsLoading,
    translationsLoaded,
    isChangingLanguage,
    fetchLanguages,
    fetchTranslations,
    setLanguage,
    getCurrentLanguageData,
    t,
    getMissingKeys,
    clearMissingKeys
  }
})

